import React, { useMemo } from 'react';
import { SCENARIO_COLORS } from '../../config/brandColors';

// Minimum votes to show cluster title label
const MIN_VOTES_FOR_LABEL = 3;

/**
 * ClusterConnectionLayer - Renders semantic cluster connections between nodes
 *
 * Shows dashed curved lines connecting nodes that belong to the same semantic cluster.
 * Cross-scenario clusters (pivot points) are shown in purple.
 * Single-scenario clusters use the scenario's theme color.
 */
const ClusterConnectionLayer = ({ clusters, positions, enabled = true }) => {
  // Generate connection paths and cluster labels
  const { connections, clusterLabels } = useMemo(() => {
    if (!enabled || !clusters || !positions || clusters.length === 0) {
      return { connections: [], clusterLabels: [] };
    }

    const cardWidth = 360;
    const cardHeight = 90;
    const connectionResult = [];
    const labelResult = [];

    clusters.forEach(cluster => {
      if (!cluster.memberIds || cluster.memberIds.length < 2) return;

      // Only draw connections for members that have positions
      const membersWithPositions = cluster.memberIds.filter(id => positions[id]);
      if (membersWithPositions.length < 2) return;

      // Color based on whether it's a cross-scenario cluster (pivot point)
      // For single-scenario clusters, use the scenario's theme color
      let color = '#9c27b0'; // Default purple for pivot points
      if (!cluster.isCrossScenario) {
        // Try to get scenario color from scenarioNames
        const scenarioName = cluster.scenarioNames?.[0]?.toLowerCase() || '';
        color = SCENARIO_COLORS[scenarioName] || '#607d8b'; // Fallback to blue-gray
      }
      const opacity = cluster.isCrossScenario ? 0.6 : 0.4;

      // Calculate cluster center for label positioning
      let sumX = 0, sumY = 0;
      membersWithPositions.forEach(id => {
        const pos = positions[id];
        sumX += pos.x + cardWidth / 2;
        sumY += pos.y + cardHeight / 2;
      });
      const centerX = sumX / membersWithPositions.length;
      const centerY = sumY / membersWithPositions.length;

      // Add label for clusters with >2 votes (MIN_VOTES_FOR_LABEL or more members)
      const voteCount = cluster.size || membersWithPositions.length;
      if (voteCount >= MIN_VOTES_FOR_LABEL && cluster.title) {
        labelResult.push({
          id: `label-${cluster.clusterId}`,
          x: centerX,
          y: centerY - 60, // Position above the center
          title: cluster.title,
          votes: voteCount,
          color,
          isCrossScenario: cluster.isCrossScenario
        });
      }

      // Generate connections between all pairs of nodes in the cluster
      // Use a "hub" approach - connect all nodes to the first node (centroid proxy)
      // This reduces visual clutter compared to connecting all pairs
      const centroidId = membersWithPositions[0];
      const centroidPos = positions[centroidId];

      membersWithPositions.slice(1).forEach(memberId => {
        const memberPos = positions[memberId];
        if (!memberPos) return;

        // Calculate center points of the cards
        const fromX = centroidPos.x + cardWidth / 2;
        const fromY = centroidPos.y + cardHeight / 2;
        const toX = memberPos.x + cardWidth / 2;
        const toY = memberPos.y + cardHeight / 2;

        // Create a curved path using quadratic bezier
        // Control point is offset perpendicular to the line
        const midX = (fromX + toX) / 2;
        const midY = (fromY + toY) / 2;

        // Offset the control point perpendicular to the line for a nice curve
        const dx = toX - fromX;
        const dy = toY - fromY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Curve offset proportional to distance, but capped
        const curveOffset = Math.min(distance * 0.2, 80);

        // Perpendicular direction (rotate 90 degrees)
        const perpX = -dy / distance;
        const perpY = dx / distance;

        // Control point
        const ctrlX = midX + perpX * curveOffset;
        const ctrlY = midY + perpY * curveOffset;

        const path = `M ${fromX},${fromY} Q ${ctrlX},${ctrlY} ${toX},${toY}`;

        connectionResult.push({
          id: `${cluster.clusterId}-${centroidId}-${memberId}`,
          path,
          color,
          opacity,
          isCrossScenario: cluster.isCrossScenario,
          clusterSize: cluster.size
        });
      });
    });

    return { connections: connectionResult, clusterLabels: labelResult };
  }, [clusters, positions, enabled]);

  // Get unique colors from connections for dynamic marker generation
  // Must be called unconditionally (before early return) to follow React hooks rules
  const uniqueColors = useMemo(() => {
    const colors = new Set(connections.map(c => c.color));
    return Array.from(colors);
  }, [connections]);

  // Generate marker ID from color (escape # for ID)
  const getMarkerId = (color) => `cluster-dot-${color.replace('#', '')}`;

  if (!enabled || connections.length === 0) {
    return null;
  }

  return (
    <g className="cluster-connections">
      <defs>
        {/* Define markers for pivot point (purple) */}
        <marker
          id="cluster-dot-9c27b0"
          viewBox="0 0 10 10"
          refX="5"
          refY="5"
          markerWidth="4"
          markerHeight="4"
        >
          <circle cx="5" cy="5" r="3" fill="#9c27b0" />
        </marker>
        {/* Dynamically generate markers for each unique color */}
        {uniqueColors.filter(c => c !== '#9c27b0').map(color => (
          <marker
            key={color}
            id={getMarkerId(color)}
            viewBox="0 0 10 10"
            refX="5"
            refY="5"
            markerWidth="4"
            markerHeight="4"
          >
            <circle cx="5" cy="5" r="3" fill={color} />
          </marker>
        ))}
      </defs>

      {connections.map(conn => (
        <path
          key={conn.id}
          d={conn.path}
          fill="none"
          stroke={conn.color}
          strokeWidth={conn.isCrossScenario ? 2 : 1.5}
          strokeOpacity={conn.opacity}
          strokeDasharray={conn.isCrossScenario ? "8,4" : "4,4"}
          strokeLinecap="round"
          markerEnd={`url(#${getMarkerId(conn.color)})`}
          style={{
            pointerEvents: 'none',
            transition: 'all 0.3s ease'
          }}
        />
      ))}
    </g>
  );
};

export default ClusterConnectionLayer;
